/**
 * Session store.
 *
 * Manages session panel state: agent selection, session management, message history, streaming.
 * Adapted from Bridge's session-store.ts for Tauri.
 *
 * Persisted fields (via localStorage under `stream.session`):
 *   - `lastAgentId` - last selected agent ID
 *   - `sessionIdByAgent` - session ID per agent
 *   - `draftsByAgent` - unsent input draft per agent
 *
 * Non-persisted (in-memory):
 *   - `messages` - current thread
 *   - `streamingState` - idle | streaming | error
 *   - `currentAgentId` - active agent
 *   - `currentSessionId` - active session
 *
 * NOTE (Task 4053b / follow-up): the message plumbing in this store
 * (`messages`, `streamingState`, `draftsByAgent`, `enqueueSend`/`pendingSend`)
 * is DEPRECATED.  `LogFeatureModal` has been migrated to `AgentSessionTranscript`
 * (Task 4053b follow-up), so there are NO remaining consumers of `MessageThread`
 * / `MessageInput`.  This plumbing is kept only to avoid a large blast-radius
 * deletion before the dead code is formally cleaned up.  Agent/session selection
 * and approval-context state in this store remain canonical for the SessionPanel.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  Message,
  MessagePart,
  ToolCall,
  ToolResult,
  MemoryOp,
  SessionArtifact,
  StreamingState,
  ApprovalContext,
} from './types'

// =============================================================================
// Ordered-parts helpers — preserve the stream's arrival order across
// text / tool_call / memory_op / artifact events so the renderer can
// show each element in its true temporal position rather than bucketing
// them after the message body.
// =============================================================================

/** Append a chunk of text to the most-recent text part, or start a new
 *  text part if the last part was a tool/memory/artifact. */
function appendTextPart(parts: MessagePart[] | undefined, text: string): MessagePart[] {
  if (!parts || parts.length === 0) {
    return [{ kind: 'text', text }]
  }
  const last = parts[parts.length - 1]
  if (last.kind === 'text') {
    return [...parts.slice(0, -1), { kind: 'text', text: last.text + text }]
  }
  return [...parts, { kind: 'text', text }]
}

/** Push a non-text part, terminating any open text segment so the next
 *  text delta starts a fresh segment AFTER this event. */
function pushPart(parts: MessagePart[] | undefined, part: MessagePart): MessagePart[] {
  return [...(parts ?? []), part]
}

/** Attach a tool result to the matching tool_call part (results arrive
 *  after their call so we patch the existing part rather than creating
 *  a new entry). Falls back to a no-op if no matching call is found. */
function attachToolResult(
  parts: MessagePart[] | undefined,
  result: ToolResult,
): MessagePart[] | undefined {
  if (!parts) return parts
  return parts.map((p) =>
    p.kind === 'tool_call' && p.toolCall.id === result.toolCallId
      ? { ...p, result }
      : p,
  )
}

interface SessionStoreState {
  // Persisted
  lastAgentId: string | null
  sessionIdByAgent: Record<string, string>
  draftsByAgent: Record<string, string>

  // Non-persisted
  messages: Message[]
  streamingState: StreamingState
  currentAgentId: string | null
  currentSessionId: string | null
  errorMessage: string | null

  // Approval context for "Ask Agent About This"
  approvalContext: ApprovalContext | null
  /** Whether the first message has been sent (to include context in payload). */
  hasContextBeenSent: boolean

  /**
   * Programmatic "send this message next" queue for an agent. Set by features
   * outside the chat input (e.g. the engineering ticket "Discuss" button) to
   * kick off a conversation. `MessageInput` watches this and, once the matching
   * agent + session are set up and streaming is idle, consumes the entry and
   * sends the message as if the operator had typed and submitted it.
   *
   * Only one pending send is queued at a time — callers should not rely on
   * queuing multiple kickoffs back-to-back.
   */
  pendingSend: { agentId: string; content: string } | null
}

interface SessionStoreActions {
  setCurrentAgent: (agentId: string, sessionId: string) => void
  setCurrentSession: (sessionId: string) => void
  newSession: (agentId: string, newSessionId: string) => void
  addMessage: (message: Message) => void
  updateMessage: (id: string, updates: Partial<Message>) => void
  appendStreamingContent: (messageId: string, content: string) => void
  addToolCall: (messageId: string, toolCall: ToolCall) => void
  addToolResult: (messageId: string, result: ToolResult) => void
  addMemoryOp: (messageId: string, memOp: MemoryOp) => void
  addArtifact: (messageId: string, artifact: SessionArtifact) => void
  setStreamingState: (state: StreamingState) => void
  setErrorMessage: (message: string | null) => void
  clearMessages: () => void
  setDraft: (agentId: string, draft: string) => void
  getDraft: (agentId: string) => string
  clearDraft: (agentId: string) => void
  reset: () => void

  // Approval context actions
  setApprovalContext: (context: ApprovalContext) => void
  clearApprovalContext: () => void
  markContextSent: () => void

  // Programmatic-send queue (see `pendingSend` doc above)
  enqueueSend: (agentId: string, content: string) => void
  consumePendingSend: () => { agentId: string; content: string } | null
}

export const useSessionStore = create<SessionStoreState & SessionStoreActions>()(
  persist(
    (set, get) => ({
      // Persisted
      lastAgentId: null,
      sessionIdByAgent: {},
      draftsByAgent: {},

      // Non-persisted
      messages: [],
      streamingState: 'idle',
      currentAgentId: null,
      currentSessionId: null,
      errorMessage: null,
      approvalContext: null,
      hasContextBeenSent: false,
      pendingSend: null,

      setCurrentAgent: (agentId: string, sessionId: string) =>
        set({
          currentAgentId: agentId,
          currentSessionId: sessionId,
          lastAgentId: agentId,
          messages: [], // Clear thread when switching agents
          streamingState: 'idle',
          errorMessage: null,
        }),

      setCurrentSession: (sessionId: string) =>
        set((state) => {
          if (state.currentAgentId) {
            return {
              currentSessionId: sessionId,
              sessionIdByAgent: {
                ...state.sessionIdByAgent,
                [state.currentAgentId]: sessionId,
              },
              messages: [],
              streamingState: 'idle',
              errorMessage: null,
            }
          }
          return state
        }),

      newSession: (agentId: string, newSessionId: string) =>
        set((state) => ({
          currentSessionId: newSessionId,
          sessionIdByAgent: {
            ...state.sessionIdByAgent,
            [agentId]: newSessionId,
          },
          messages: [],
          streamingState: 'idle',
          errorMessage: null,
        })),

      addMessage: (message: Message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      updateMessage: (id: string, updates: Partial<Message>) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === id ? { ...msg, ...updates } : msg
          ),
        })),

      appendStreamingContent: (messageId: string, content: string) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  content: msg.content + content,
                  parts: appendTextPart(msg.parts, content),
                }
              : msg
          ),
        })),

      addToolCall: (messageId: string, toolCall: ToolCall) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  toolCalls: [...(msg.toolCalls ?? []), toolCall],
                  parts: pushPart(msg.parts, { kind: 'tool_call', toolCall }),
                }
              : msg
          ),
        })),

      addToolResult: (messageId: string, result: ToolResult) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  toolResults: [...(msg.toolResults ?? []), result],
                  parts: attachToolResult(msg.parts, result),
                }
              : msg
          ),
        })),

      addMemoryOp: (messageId: string, memOp: MemoryOp) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  memoryOps: [...(msg.memoryOps ?? []), memOp],
                  parts: pushPart(msg.parts, { kind: 'memory_op', memoryOp: memOp }),
                }
              : msg
          ),
        })),

      addArtifact: (messageId: string, artifact: SessionArtifact) =>
        set((state) => ({
          messages: state.messages.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  artifacts: [...(msg.artifacts ?? []), artifact],
                  parts: pushPart(msg.parts, { kind: 'artifact', artifact }),
                }
              : msg
          ),
        })),

      setStreamingState: (state: StreamingState) => set({ streamingState: state }),

      setErrorMessage: (message: string | null) => set({ errorMessage: message }),

      clearMessages: () =>
        set({
          messages: [],
          streamingState: 'idle',
          errorMessage: null,
        }),

      setDraft: (agentId: string, draft: string) =>
        set((state) => ({
          draftsByAgent: {
            ...state.draftsByAgent,
            [agentId]: draft,
          },
        })),

      getDraft: (agentId: string) => {
        const state = get()
        return state.draftsByAgent[agentId] ?? ''
      },

      clearDraft: (agentId: string) =>
        set((state) => {
          const { [agentId]: _, ...rest } = state.draftsByAgent
          return { draftsByAgent: rest }
        }),

      reset: () =>
        set({
          messages: [],
          streamingState: 'idle',
          currentAgentId: null,
          currentSessionId: null,
          errorMessage: null,
          approvalContext: null,
          hasContextBeenSent: false,
          pendingSend: null,
        }),

      // Approval context actions
      setApprovalContext: (context: ApprovalContext) =>
        set({
          approvalContext: context,
          hasContextBeenSent: false,
        }),

      clearApprovalContext: () =>
        set({
          approvalContext: null,
          hasContextBeenSent: false,
        }),

      markContextSent: () =>
        set({
          hasContextBeenSent: true,
        }),

      enqueueSend: (agentId: string, content: string) =>
        set({ pendingSend: { agentId, content } }),

      consumePendingSend: () => {
        const pending = get().pendingSend
        if (pending) {
          set({ pendingSend: null })
        }
        return pending
      },
    }),
    {
      name: 'stream.session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lastAgentId: state.lastAgentId,
        sessionIdByAgent: state.sessionIdByAgent,
        draftsByAgent: state.draftsByAgent,
      }),
      version: 1,
    }
  )
)

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Generate a new session ID.
 * Format: `sess_` + 21-char random (matches admin-portal).
 */
export function generateSessionId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const random = Array.from({ length: 21 }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join('')
  return `sess_${random}`
}
